import { APIResponseType } from 'app/apiService/types';
import apiService from '..';

// Lease terms interface
export interface LeaseTerms {
    monthly_payment: number;
    due_at_signing: number;
    lease_term_months: number;
    annual_mileage_limit: number;
}

// Finance terms interface
export interface FinanceTerms {
    apr: number;
    term_months: number;
    down_payment: number;
}

// Picture interface based on schema
export interface Picture {
    url: string;
    path: string;
}

// Offer Card - Essential fields for card display
export interface OfferCard {
    _id: string;
    title: string;
    headline: string;
    content: string;
    name?: string;
    description?: string;
    offer_type: 'LEASE' | 'FINANCE' | 'CASH';
    offer_image_url?: string;
    offer_url?: string;
    vehicle_make: string;
    vehicle_model: string;
    vehicle_year: number;
    image?: string; // Optional - for backward compatibility
    picture?: Picture; // New field based on schema
    msrp: number;
    selling_price: number;
    drivetrain: string;
    trim: string;
    is_active: boolean;
    start_date: string;
    end_date: string;
    stock_number?: string;
    model_code?: string;
    // For lease offers - array of multiple lease options
    lease_terms?: LeaseTerms[];
    // For finance offers  
    finance_terms?: FinanceTerms;
    // For cash offers
    cashback_amount?: number;
    dealership_id: string;
    vin?: string;
    disclaimer?: string;
    eligible_regions?: string[];
    conditions?: string[];
    created_at?: string;
    updated_at?: string;
}

// Offer Details - Complete offer information
export interface OfferDetails extends OfferCard {
    // All fields are now included in OfferCard based on schema
}

interface OfferPayload extends Partial<OfferDetails> {
    title: string; // Required field for creating offers
}

interface GetAllOffersParams {
    skip?: number;
    limit?: number;
    search?: string;
    sort?: string; // MUI DataGrid sort format JSON string
    status?: string;
    filters?: string; // MUI DataGrid filter format JSON string
    dealer?: string; // Add dealership_id for filtering
    // Add more filter parameters as needed
}

class Offers {
    static ENDPOINT = 'api/v2/offer';

    static async getOffer(offerId: string): Promise<APIResponseType> {
        if (!offerId) throw new Error("Offer ID is required to fetch details");
        try {
            const response = await apiService.get<APIResponseType>(`${this.ENDPOINT}/${offerId}`);
            return response;
        } catch (error) {
            throw error;
        }
    }

    static async getAllOffers(params?: GetAllOffersParams): Promise<APIResponseType> {
        try {
            const queryParams = new URLSearchParams();

            if (params) {
                if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
                if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
                if (params.search) queryParams.append('search', params.search);
                if (params.sort) queryParams.append('sort', params.sort);
                if (params.status) queryParams.append('status', params.status);
                if (params.filters) queryParams.append('filters', params.filters);
                if (params.dealer) queryParams.append('dealer', params.dealer);
            }

            const queryString = queryParams.toString();
            const url = queryString ? `${this.ENDPOINT}?${queryString}` : this.ENDPOINT;

            const response = await apiService.get<APIResponseType>(url);
            return response;
        } catch (error) {
            throw error;
        }
    }

    static async addOffer(payload: OfferPayload): Promise<APIResponseType> {
        try {
            console.log("API Service - addOffer called with payload:", payload);
            const response = await apiService.post<APIResponseType>(`${this.ENDPOINT}`, payload);
            await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
            return response;
        } catch (error) {
            console.error("API Service - addOffer error:", error);
            throw error;
        }
    }

    static async deleteOffer(offerId: string): Promise<APIResponseType> {
        if (!offerId) throw new Error("Offer ID is required for deletion");
        try {
            const response = await apiService.delete<APIResponseType>(`${this.ENDPOINT}/${offerId}`);
            await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
            return response;
        } catch (error) {
            throw error;
        }
    }

    static async editOffer(offerId: string, payload: Partial<OfferPayload>): Promise<APIResponseType> {
        if (!offerId) throw new Error("Offer ID is required for editing");
        if (!Object.keys(payload).length) throw new Error("Edit payload cannot be empty");

        try {
            const response = await apiService.put<APIResponseType>(`${this.ENDPOINT}/${offerId}`, payload);
            await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
            return response;
        } catch (error) {
            throw error;
        }
    }
}

export default Offers; 